/**
 * Браузерные уведомления о входящих сообщениях.
 *
 * ЧТО ЗДЕСЬ ПРОИСХОДИТ и почему именно так:
 *
 * 1. Источник событий — RoomEvent.Timeline. По нему прилетают и
 *    ИСТОРИЧЕСКИЕ события (initial sync, пагинация назад), и формально
 *    они «живые» (liveEvent=true). Защита двухуровневая: (а) молчим до
 *    первого завершённого sync (SyncState PREPARED) — это закрывает
 *    лавину после логина; (б) пропускаем всё с toStartOfTimeline=true или
 *    data.liveEvent === false — это закрывает пагинацию назад.
 *
 * 2. ПРАВИЛА ТИШИНЫ И МОДЕЛЬ (постановки 13:31 и 13:49):
 *    - свои сообщения не уведомляем;
 *    - комната открыта И окно в фокусе — молчим (человек и так видит);
 *    - уведомляем ТОЛЬКО об адресованном лично: личка 1х1, упоминание
 *      (highlight-твик push-правил), ответ в треде, корень которого мой.
 *      Обычное групповое сообщение НЕ уведомляет ни всплывашкой, ни звуком
 *      — о нём говорит жирная строка в списке. Это же снимает лавину;
 *    - зашифрованные комнаты: тело показать не можем (crypto выключен),
 *      но факт «в комнате X новое сообщение» показать можно и нужно.
 *
 * 3. PUSH-ПРАВИЛА Matrix (пункт 5 постановки): решение «уведомлять ли»
 *    принимает НЕ наш код, а client.getPushActionsForEvent(ev) — это те же
 *    правила, что работают в Element (mute комнаты = room-правило с
 *    dont_notify). Мы только исполняем: notify=false — молчим; есть
 *    tweak sound — играем. Имена сверены по исходнику SDK v42.3.0:
 *    pushprocessor.ts (IActionsObject { notify, tweaks }) и client.d.ts
 *    (сигнатура getPushActionsForEvent(ev, forceRecalculate?)).
 *
 * 4. Заголовок/тело/иконка: имя отправителя + комната, тело — урезанный
 *    текст сообщения. Иконка — аватар отправителя; наш media-сервер требует
 *    авторизации (см. media.ts), поэтому mxc:// качаем в blob:-URL.
 */
import {
  EventStatus,
  ClientEvent,
  EventType,
  RoomEvent,
  type IRoomTimelineData,
  type MatrixClient,
  type MatrixEvent,
  type Room,
  TweakName,
} from "matrix-js-sdk";

import { tr } from "../i18n";
import { fetchAuthedBlobUrl } from "../matrix/media";
import { displayBody } from "../matrix/mentions";
import { relatesToOf, replyTargetId } from "../matrix/relations";
import { directRoomIds, senderLabel } from "../matrix/rooms";
import { playIncomingSoundForRoom } from "./sound";
import { loadNotificationSettings, roomNotificationSetting, type NotificationSettings } from "./settings";
import { appPermalink } from "../ui/router";
import { deliverSystemNotification, type AppNotificationOptions } from "./systemNotification";

/** Максимальная длина тела уведомления — дальше обрезаем с многоточием. */
const BODY_LIMIT = 120;

/**
 * Контекст, который React-подписчик обязан держать актуальным.
 * Читается ТОЛЬКО через геттеры в момент события, а не захватывается
 * в замыкание — иначе «открытая комната» в обработчике устаревала бы
 * на каждый переход.
 */
export interface NotifierContext {
  /** id открытой сейчас комнаты или null. */
  getActiveRoomId(): string | null;
  /** Окно сейчас видимо и в фокусе (useWindowActive из App). */
  isWindowActive(): boolean;
  /** Открыть комнату по клику на уведомлении. */
  openRoom(roomId: string): void;
}

function isDirectRoom(room: Room, directIds: Set<string>): boolean {
  // Та же формула, что в rooms.ts (Антон 2026-09-17): личка = не больше
  // двух участников; m.direct сам по себе комнату личной не делает.
  const joined = room.getJoinedMemberCount();
  return !room.isSpaceRoom() && joined <= 2 &&
    (directIds.has(room.roomId) || Boolean(room.getDMInviter()) || joined === 2);
}

function mentionOf(ev: MatrixEvent, me: string | null, highlighted: boolean): boolean {
  const mentions = ev.getContent()["m.mentions"] as
    | { user_ids?: string[]; room?: boolean }
    | undefined;
  return Boolean(
    highlighted || mentions?.room ||
    (me && Array.isArray(mentions?.user_ids) && mentions.user_ids.includes(me)),
  );
}

/**
 * Это ответ НА МОЁ сообщение?
 *
 * Связь читаем через `replyTargetId` (relations.ts), а НЕ через
 * `ev.getContent()["m.relates_to"]`. В ЗАШИФРОВАННОЙ комнате `m.relates_to`
 * вынесен из шифруемого тела в wire content, а `getContent()` у
 * расшифрованного события отдаёт clearEvent.content, где связи нет ␲овсе.
 * Замер N2 (.probe-reply, 2026-09-21): до правки ответ на моё сообщение
 * в шифрованной комнате не давал уведомления вообще.
 *
 * В незашифрованной комнате старый код работал (он читал content
 * напрямую, а не через getRelation()) — здесь добавлены только wire
 * content и отсев тредового фолбэка; ветка «корень треда мой» ниже
 * осталась как была и продолжает покрывать фолбэк-реплики в моём треде.
 */
function replyToMine(room: Room, ev: MatrixEvent, me: string | null): boolean {
  if (!me) return false;
  const replyId = replyTargetId(ev);
  if (replyId && room.findEventById(replyId)?.getSender() === me) return true;
  const rootId = ev.threadRootId;
  return Boolean(rootId && room.getThread(rootId)?.rootEvent?.getSender() === me);
}

function reactionToMine(room: Room, ev: MatrixEvent, me: string | null): boolean {
  if (!me || ev.getType() !== "m.reaction") return false;
  // Тот же источник связи, что и у реплаев. Для незашифрованного события
  // wire content и content — одно и то же, так что поведение не меняется;
  // выигрыш — реакция, зашифрованная клиентом, который так умеет.
  const relates = relatesToOf(ev);
  return Boolean(relates?.event_id && room.findEventById(relates.event_id)?.getSender() === me);
}

function categoryAllows(
  settings: NotificationSettings,
  room: Room,
  ev: MatrixEvent,
  me: string | null,
  highlighted: boolean,
  directIds: Set<string>,
): boolean {
  const mode = roomNotificationSetting(room.roomId)?.mode;
  if (mode === "mute") return false;
  if (reactionToMine(room, ev, me)) return settings.categories.reactions;

  const direct = isDirectRoom(room, directIds);
  const mention = mentionOf(ev, me, highlighted);
  const reply = replyToMine(room, ev, me);
  if (direct) return settings.categories.directMessages;
  if (mode === "mentions") {
    return (mention && settings.categories.mentions) || (reply && settings.categories.replies);
  }
  if (mention || reply) {
    return (mention && settings.categories.mentions) || (reply && settings.categories.replies);
  }
  if (mode === "all") return true;
  return settings.categories.groupMessages;
}

/**
 * Подписаться на входящие и показывать браузерные уведомления.
 * Возвращает функцию отписки.
 */
export function startNotifier(client: MatrixClient, ctx: NotifierContext): () => void {
  const me = client.getUserId();
  // Список личных комнат для правила «1х1 адресовано мне всегда». Читаем
  // лениво при первом событии и кэшируем: m.direct меняется редко, а
  // перечитывать account data на каждое сообщение — лишняя работа.
  // Кэш НЕ навсегда: invalidate по AccountData-событию ниже.
  let directIds: Set<string> | null = null;
  const directRooms = () => {
    if (!directIds) directIds = directRoomIds(client);
    return directIds;
  };
  const onAccountData = () => {
    directIds = null;
  };
  client.on(ClientEvent.AccountData, onAccountData);

  /**
   * ЛАВИНА ПОСЛЕ ЛОГИНА (пункт 4 постановки) — самая важная защита модуля.
   * События INITIAL SYNC приходят через addLiveEvents и эмитятся с
   * data.liveEvent === true — формально они «живые», и простой проверки
   * liveEvent мало: после логина прилетели бы десятки уведомлений за всю
   * историю. Поэтому молчим, пока клиент не дошёл до состояния PREPARED —
   * это сигнал «первый sync обработан ЦЕЛИКОМ» (SyncState.PREPARED,
   * sync.ts). События ДО него — история, после — живые. При реконнекте
   * (limited sync) состояние PREPARED уже было, и пропущенные за офлайн
   * сообщения придут как живые — о них уведомить правильно.
   */
  let firstSyncDone = client.getSyncState() === "PREPARED" || client.getSyncState() === "SYNCING";
  const onSync = (state: string) => {
    if (state === "PREPARED" || state === "SYNCING") firstSyncDone = true;
  };
  client.on(ClientEvent.Sync, onSync);

  const onTimeline = (
    ev: MatrixEvent,
    room: Room | undefined,
    // ВАЖНО (tsc поймал): на MatrixClient это событие пробрасывается
    // reEmitter'ом, и в его подписи toStartOfTimeline/data допускают
    // undefined. Сигнатура обработчика обязана это повторять, иначе
    // client.on(...) не принимает функцию.
    toStartOfTimeline: boolean | undefined,
    _removed: boolean,
    data: IRoomTimelineData,
  ) => {
    // Историю (initial sync, пагинация назад) пропускаем — см. шапку файла.
    if (!firstSyncDone) return;
    if (toStartOfTimeline || data?.liveEvent === false) return;
    if (!room) return;

    // Messages and reactions to our own messages are separately configurable.
    if (ev.getType() !== EventType.RoomMessage && ev.getType() !== "m.reaction") return;

    // Локальное эхо нашего же сообщения: не только «свои не уведомляем»,
    // но и не уведомляем то, что ещё не уехало на сервер.
    if (ev.getSender() === me) return;
    // isLocalEcho() в v42.3.0 на типе MatrixEvent НЕ объявлен (tsc это
    // поймал). Эхо определяем по status: у неотправленного события он
    // SENDING/QUEUED, у пришедшего с сервера — null.
    if (ev.status === EventStatus.SENDING || ev.status === EventStatus.QUEUED) return;

    const settings = loadNotificationSettings();

    // Enabled notification categories apply even while this room is open.
    // Anton explicitly expects an app/system notification during foreground
    // testing; silently suppressing it made working permissions look broken.

    // PUSH-ПРАВИЛА. getPushActionsForEvent может вернуть null (правила ещё
    // не приехали от сервера в первые секунды sync) — в этом случае
    // действуем как notify=true БЕЗ звука: пропустить сообщение хуже,
    // чем показать уведомление без персональных твиков.
    const actions = client.getPushActionsForEvent(ev);
    if (actions && !actions.notify) return; // mute комнаты / dont_notify

    if (!categoryAllows(
      settings,
      room,
      ev,
      me,
      actions?.tweaks?.[TweakName.Highlight] === true,
      directRooms(),
    )) {
      return;
    }


    // Звук — по tweak из правил (TweakName.Sound), и только если человек
    // не выключил звук в настройках. Правило «комната открыта и в фокусе»
    // выше уже сработало раньше, так что сюда доходят только фоновые случаи.
    // ИСПРАВЛЕНО 14:42 (проверка Антона: «звука не было»). Раньше звук
    // требовал tweak sound от push-правил, и при actions === null (правила
    // ещё не доехали) или при нестандартном наборе правил молчал. Но сюда
    // событие доходит, только пройдя строгий фильтр addressedToMe — значит
    // оно точно адресовано человеку, и звук уместен. Push-правила остаются
    // старшими в вопросе «уведомлять ли вообще» (notify === false выше).
    if (settings.sound) {
      playIncomingSoundForRoom(room.roomId);
    }

    // System notification: the actual delivery attempt is authoritative for
    // the installed app; wrapper permission strings can be stale.
    if (!settings.enabled) return;
    void showNotification(client, room, ev, ctx).catch((error) => {
      console.warn("[notifications] system delivery failed:", error);
    });
  };

  client.on(RoomEvent.Timeline, onTimeline);
  return () => {
    client.off(RoomEvent.Timeline, onTimeline);
    client.off(ClientEvent.Sync, onSync);
    client.off(ClientEvent.AccountData, onAccountData);
  };
}

/** Текст сообщения для тела уведомления, одной строкой и не длиннее лимита. */
function bodyOf(ev: MatrixEvent): string {
  const body = String(ev.getContent().body ?? "");
  const mentionsRaw = ev.getContent()["m.mentions"] as { user_ids?: string[] } | undefined;
  // Отправителя из упоминаний убираем (это «ответ самому себе» в шапке
  // реплая), а вот себя-получателя — НЕ убираем (Антон 2026-10-09):
  // уведомление «ab: начинаю переключение?» обязано показать «@ab …».
  const mentions = (mentionsRaw?.user_ids ?? [])
    .filter((userId) => userId !== ev.getSender())
    .map((userId) => ({
      userId,
      displayName: userId,
    }));
  const rawFormatted = ev.getContent().formatted_body;
  // Имя для «Имя:» → «@Имя» берём из formatted_body (текст ссылки на
  // момент отправки) — там настоящее имя, а не MXID (Антон 2026-10-09).
  const display = displayBody(
    body,
    mentions.length > 0 ? mentions : null,
    typeof rawFormatted === "string" ? rawFormatted : null,
  );
  const oneLine = display.replace(/\s+/g, " ").trim();
  if (oneLine.length <= BODY_LIMIT) return oneLine;
  return oneLine.slice(0, BODY_LIMIT - 1) + "…";
}

async function showNotification(
  client: MatrixClient,
  room: Room,
  ev: MatrixEvent,
  ctx: NotifierContext,
): Promise<void> {
  const sender = senderLabel(room, ev);
  const roomName = room.name || room.roomId;
  const title = `${sender} · ${roomName}`;

  // Иконка: аватар отправителя. mxc:// требует авторизации на нашем сервере,
  // поэтому blob. Нет аватара/ошибка загрузки — уведомление всё равно уходит,
  // просто без иконки.
  const senderId = ev.getSender();
  const member = senderId ? room.getMember(senderId) : null;
  // getMxcAvatarUrl(): string | undefined (lib/models/room-member.d.ts).
  const mxc = member?.getMxcAvatarUrl() ?? null;
  // Avatar media is optional. A failed authenticated media request must not
  // turn a perfectly valid message notification into a delivery failure.
  // (Sound and unread state are updated before this function, which otherwise
  // makes this failure look exactly like "only system notifications broke".)
  let icon: string | null = null;
  if (mxc) {
    try {
      icon = await fetchAuthedBlobUrl(client, mxc);
    } catch (error) {
      console.warn("[notifications] sender avatar unavailable; delivering without it:", error);
    }
  }

  const body = bodyOf(ev);

  const options: AppNotificationOptions = {
    body: body || tr("notification.newMessage"),
    ...(icon ? { icon } : {}),
    // tag по комнате: новое сообщение в той же комнате ЗАМЕНЯЕТ прежнее
    // уведомление, а не складывается стопкой — как в Telegram Desktop.
    tag: `alg-room-${room.roomId}`,
    renotify: true,
    silent: false,
    data: { url: appPermalink(room.roomId) },
  };

  const onClick = (notification: Notification) => {
    window.focus();
    ctx.openRoom(room.roomId);
    notification.close();
  };
  try {
    try {
      await deliverSystemNotification(title, options, onClick);
    } catch (error) {
      // Some installed-app notification services reject blob: icons even
      // though the authenticated avatar download itself succeeded. The icon
      // is decorative, so retry once without it before declaring delivery
      // failed.
      if (!icon) throw error;
      console.warn("[notifications] notification icon rejected; retrying without it:", error);
      const optionsWithoutIcon = { ...options };
      delete optionsWithoutIcon.icon;
      await deliverSystemNotification(title, optionsWithoutIcon, onClick);
    }
  } finally {
    if (icon) URL.revokeObjectURL(icon);
  }
}
